import { useAuth } from "@clerk/clerk-react";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import pic from "assets/splashpage/vamoneybacksplash2.png";
import { OrganizationUserContext } from "contexts/Organization";
import { SplashPage, SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { SearchParams } from "schemas/dashboard";
import { Plans } from "schemas/payments";
import { routes } from "schemas/routes";

import { PAGE_VISITED } from "constants/trackingProps";
import { AuthAction } from "features/Auth/schema";
import { fetcherAuth } from "utils/api";
import { getParams, getSource, identifyUser, trackEvent } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

import VaAnnualAuth from "./Auth";
import ProgramInfo from "./ProgramInfo";
import VAMoneyBackBanner from "./ProgramInfo/Banner";

interface Props {
  hasBookCall: boolean;
}

const VaAnnualSplashPage = ({ hasBookCall }: Props) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isSignedIn = getParams("signed_in") === "true";
  const navigate = useNavigate();
  const source = getSource();

  const { getToken, isLoaded } = useAuth();

  const { currentOrg, setCurrentOrg, setCurrentUser } = useContext(
    OrganizationUserContext,
  );
  const { subscription, setSubscription } = useContext(SubscriptionContext);
  const hasAccessToVaYearly =
    subscription?.isSubscribed &&
    subscription?.subscription?.planName === Plans.VA_YEARLY;

  const { handleCheckout } = useSubscription();
  const [fetchUserLoading, setFetchUserLoading] = useState(false);

  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const successCheckout = getParams("checkout") === "success";

  const affiliateCode = searchParams.get(SearchParams.AFFILIATE_CODE);

  const fetchUserInfo = async () => {
    try {
      setFetchUserLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/auth/user?affiliate_code=${affiliateCode || ""}`,
      );
      identifyUser(res.organizationUser.id);
      setCurrentOrg(res.organization);
      setSubscription(res.subscription);
    } catch (error) {
      handleClerkUserSignUp();
    } finally {
      setFetchUserLoading(false);
    }
  };

  const handleClerkUserSignUp = async () => {
    setFetchUserLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/auth/signup`,
        "POST",
        {},
        {
          source,
          affiliateCode,
        },
      );
      identifyUser(res.organizationUser.id);
      setCurrentUser(res.organizationUser);
      setCurrentOrg(res.organization);
      if (
        // @ts-ignore
        typeof window.fbq !== "undefined" &&
        // @ts-ignore
        typeof window.fbq === "function"
      ) {
        // @ts-ignore
        window.fbq("track", "CompleteRegistration");
      }
      if (
        // @ts-ignore
        typeof window.ttq !== "undefined" &&
        // @ts-ignore
        typeof window.ttq.track === "function"
      ) {
        // @ts-ignore
        window.ttq.track("CompleteRegistration");
      }
      setFetchUserLoading(false);
    } catch (error) {
      setFetchUserLoading(false);
    }
  };

  const checkoutVaBundle = async () => {
    if (hasAccessToVaYearly) {
      return;
    }
    if (!currentOrg?.id) {
      if (hasBookCall) {
        navigate(`/${routes.vaAnnualBooked}/sign-up${location.search}`, {
          replace: true,
        });
      } else {
        navigate(`/${routes.vaAnnual}/sign-up${location.search}`, {
          replace: true,
        });
      }
    } else {
      handleCheckout(
        Plans.VA_YEARLY,
        hasBookCall ? routes.vaAnnualBooked : routes.vaAnnual,
        source || "",
        SplashPage.va_money_back,
      );
    }
  };

  useEffect(() => {
    trackEvent(PAGE_VISITED);
    if (!currentOrg?.id) {
      fetchUserInfo();
    } else if (isSignedIn && currentOrg?.id) {
      checkoutVaBundle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, currentOrg?.id, subscription?.subscription?.planName]);

  useEffect(() => {
    if (successCheckout) {
      navigate(`/${routes.dashboard}${location.search}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successCheckout]);

  return fetchUserLoading || !isLoaded ? (
    <Backdrop
      sx={{
        backgroundColor: "white",
        zIndex: 9999,
        position: "absolute",
      }}
      open={fetchUserLoading}
      appear={false}
    >
      <CircularProgress />
    </Backdrop>
  ) : (
    <Grid
      container
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflowY: "auto",
        backgroundColor: "#f5f5f5",
      }}
    >
      <VAMoneyBackBanner checkoutBundle={checkoutVaBundle} />
      <Grid
        container
        justifyContent={"flex-end"}
        sx={{ position: "fixed", top: 50, right: 20 }}
      >
        <Box>
          <Button
            sx={{ color: "black" }}
            onClick={() => {
              trackEvent(
                "VA Money Back Splash Page - Bento Home Button Clicked",
              );
              window.open("https://www.onbento.com?s=va_money_back");
            }}
          >
            Bento Home
          </Button>
          <Button
            sx={{ color: "black" }}
            onClick={() => {
              trackEvent(
                "VA Money Back Splash Page - Bento Instagram Button Clicked",
              );
              window.open("https://www.instagram.com/onbento");
            }}
          >
            Instagram
          </Button>
        </Box>
      </Grid>
      <Grid
        item
        xs
        md
        lg
        container
        justifyContent={"center"}
        alignItems={"center"}
      >
        <Routes>
          <Route
            path="/"
            element={
              <ProgramInfo
                fetchUserLoading={fetchUserLoading || !isLoaded}
                checkoutBundle={checkoutVaBundle}
                hasAccessToVaYearly={hasAccessToVaYearly}
                hasBookCall={hasBookCall}
              />
            }
          />
          <Route
            path="/sign-in/*"
            element={
              <VaAnnualAuth
                hasBookedCall={hasBookCall}
                action={AuthAction.SIGN_IN}
              />
            }
          />
          <Route
            path="/sign-up/*"
            element={
              <VaAnnualAuth
                hasBookedCall={hasBookCall}
                action={AuthAction.SIGN_UP}
              />
            }
          />
        </Routes>
      </Grid>
      {!isMobileScreen && (
        <Grid
          item
          xs
          lg
          sx={{ mt: 10, mb: 4 }}
          container
          alignItems={"center"}
          justifyContent="center"
          direction="column"
        >
          <Grid item>
            <Box
              component="img"
              src={pic}
              sx={{ maxWidth: { md: 400, lg: 600, xl: 700 }, borderRadius: 4 }}
            />
          </Grid>
          <Typography textAlign="center" sx={{ my: 1 }}>
            {" "}
            <em>
              Virtual Assistant users are{" "}
              <Box
                component="span"
                sx={{ color: "success.main", fontWeight: 600 }}
              >
                33x more likely
              </Box>{" "}
              to land brand deals.
            </em>
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

export default VaAnnualSplashPage;
